import { useCallback, useEffect, useState } from 'react';
import { createRoutesFromElements, Route, RouterProvider } from 'react-router-dom';
import Banner from '../common/Banner/Banner';
import Layout from '../common/Layout/Layout';
import RouteErrorBoundary from '../common/RouteErrorBoundary/RouteErrorBoundary';
import BookingContext, { initialBookingState } from '../contexts/BookingContext/BookingContext';
import useHotelConfig from '../hooks/useHotelConfig/useHotelConfig';
import useToken from '../hooks/useToken/useToken';
import useTomTomKey from '../hooks/useTomTomKey/useTomTomKey';
import BookingConfirmation from '../pages/BookingConfirmation/BookingConfirmation';
import Design from '../pages/Design/Design';
import Destination from '../pages/Destination/Destination';
import Email from '../pages/Email/Email';
import Home from '../pages/Home/Home';
import Login from '../pages/Login/Login';
import Name from '../pages/Name/Name';
import Passengers from '../pages/Passengers/Passengers';
import Phone from '../pages/Phone/Phone';
import Receipt from '../pages/Receipt/Receipt';
import { sentryCreateBrowserRouter } from '../sentry';
import appConfig from '../utils/appConfig';
import { sleep } from '../utils/sleep';
import './App.scss';

const MAX_TOMTOM_LOAD_ATTEMPTS = 3;
const TOMTOM_RETRY_DELAY_MS = 1000;

const App = () => {
  const { token, setToken } = useToken();
  const { hotelConfiguration } = useHotelConfig();
  const { apiKey, load } = useTomTomKey();
  const [booking, setBooking] = useState(initialBookingState);

  const checkToken = useCallback(
    (e: StorageEvent) => {
      if (e.key === 'token') setToken(e.newValue);
    },
    [setToken],
  );

  //@ts-expect-error is actually a CustomEvent but TS is unhappy about it
  const handleTokenChangeEvent = e => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (e.detail) localStorage.setItem('logout-message', JSON.stringify(e.detail));
  };

  /*
   * Handles when another tab clears the token
   */
  useEffect(() => {
    window.addEventListener('storage', checkToken);
    window.addEventListener('token-change-message', handleTokenChangeEvent);

    return () => {
      window.removeEventListener('storage', checkToken);
      window.removeEventListener('token-change-message', handleTokenChangeEvent);
    };
  }, [checkToken]);

  const [tomtomLoadFailed, setTomtomLoadFailed] = useState(false);

  const loadTomTomWithRetry = useCallback(
    async (signal: AbortSignal) => {
      for (let attempt = 1; attempt <= MAX_TOMTOM_LOAD_ATTEMPTS; attempt += 1) {
        if (signal.aborted) return;
        try {
          await load(signal);
          return;
        } catch {
          if (signal.aborted) return;
          if (attempt === MAX_TOMTOM_LOAD_ATTEMPTS) {
            setTomtomLoadFailed(true);
            return;
          }
          await sleep(TOMTOM_RETRY_DELAY_MS);
        }
      }
    },
    [load],
  );

  useEffect(() => {
    if (!token || apiKey) return;

    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadTomTomWithRetry(controller.signal);

    return () => {
      controller.abort();
    };
  }, [token, apiKey, loadTomTomWithRetry]);

  const router = sentryCreateBrowserRouter(
    createRoutesFromElements(
      <Route errorElement={<RouteErrorBoundary />}>
        {appConfig('VITE_ENV') !== 'Production' && appConfig('VITE_ENV') !== 'uat' && (
          <Route path="design" element={<Design />} />
        )}
        <Route element={<Layout />}>
          <Route errorElement={<RouteErrorBoundary />}>
            {!token && <Route path="*" element={<Login />} />}
            {token && (
              <>
                <Route path="/" element={<Home />} />
                {hotelConfiguration?.data && (
                  <>
                    <Route path="/name" element={<Name />} />
                    <Route path="/phone-number" element={<Phone />} />
                    <Route path="/destination" element={<Destination />} />
                    <Route path="/passengers" element={<Passengers />} />
                    <Route path="/receipt" element={<Receipt />} />
                    <Route path="/email" element={<Email />} />
                    <Route path="/booking-confirmation" element={<BookingConfirmation />} />
                  </>
                )}
                <Route path="*" element={<div className="404">404 Page Not Found</div>} />
              </>
            )}
          </Route>
        </Route>
      </Route>,
    ),
  );

  return (
    <BookingContext.Provider value={{ booking, setBooking }}>
      {tomtomLoadFailed && (
        <div className="tomtom-load-error-banner">
          <Banner
            type="error"
            message="We could not prepare address search. You may need to enter your destination manually, or refresh to try again."
            dismissible
          />
        </div>
      )}
      <RouterProvider router={router} />
    </BookingContext.Provider>
  );
};

export default App;
